import { useIntl } from 'react-intl';
import { Check } from 'lucide-react';
import { foundation } from '../../../ui/tokens';

/**
 * Numbered pill stepper for the manual invoice wizard.
 *
 * Renders one pill per step with a connector line between them. Each pill
 * has three states:
 *   - completed → filled brand-color circle with a check icon
 *   - active    → filled brand-color circle with the step number
 *   - pending   → outlined neutral circle with the step number
 *
 * Connectors fill in brand color as the user progresses through the wizard.
 *
 * Replaces the previous "Step 1 of 3" plain-text line + the thin progress
 * bar inside ManualInvoiceStepHeader — gives the user a clearer sense of
 * where they are and how much is left without taking much vertical space.
 *
 * Props:
 *   currentStep — 1-indexed current step (1, 2, or 3)
 *   steps       — array of { key, label } describing each step in order
 *   borderless  — opt-in: drop the bottom hairline (a caption row under it owns the divider)
 *   flush       — opt-in (KAN-1182): no padding, no background, no hairline, so the dots sit flush
 *                 inside a pop-up that already has its own gutter. Default false = unchanged.
 *   compact     — when true, hide pill labels (numbers/checks only). Use on
 *                 mobile where labels ellipsize; aria-label keeps a11y.
 */
export default function ManualInvoiceStepIndicator({ currentStep, steps, compact = false, borderless = false, flush = false }) {
  const intl = useIntl();
  const { color, fontFamily, fontSize, fontWeight, spacePx } = foundation;
  const total = steps.length;

  return (
    <div
      role="group"
      aria-label={intl.formatMessage({ id: 'invoice.wizard.progressAria', defaultMessage: 'Wizard progress' })}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacePx[1],
        padding: flush ? 0 : `${spacePx[3]} ${spacePx[5]}`,
        borderBottom: borderless || flush ? 'none' : `1px solid ${color.neutral.border}`,
        backgroundColor: flush ? 'transparent' : color.neutral.surface,
        fontFamily: fontFamily.sans,
      }}
    >
      {steps.map((step, i) => {
        const idx = i + 1;
        const isCompleted = idx < currentStep;
        const isActive = idx === currentStep;
        const isPending = idx > currentStep;
        const isLast = i === total - 1;

        return (
          <div
            key={step.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacePx[2],
              flex: isLast ? '0 0 auto' : '1 1 auto',
              minWidth: 0,
            }}
          >
            {/* Pill */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacePx[2],
                minWidth: 0,
              }}
              aria-current={isActive ? 'step' : undefined}
              aria-label={compact ? step.label : undefined}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: fontSize.xs,
                  fontWeight: fontWeight.bold,
                  lineHeight: 1,
                  backgroundColor: (isCompleted || isActive)
                    ? color.brand.primary
                    : color.neutral.surface,
                  border: (isCompleted || isActive)
                    ? `1px solid ${color.brand.primary}`
                    : `1px solid ${color.neutral.border}`,
                  color: (isCompleted || isActive)
                    ? color.brand.onPrimary
                    : color.neutral.textMuted,
                  transition: 'background-color 180ms ease, border-color 180ms ease, color 180ms ease',
                }}
                aria-hidden
              >
                {isCompleted ? <Check size={14} strokeWidth={2.5} /> : idx}
              </span>
              {!compact && (
                <span
                  style={{
                    fontSize: fontSize.sm,
                    fontWeight: isActive ? fontWeight.semibold : fontWeight.medium,
                    color: isActive
                      ? color.neutral.text
                      : isPending
                        ? color.neutral.textMuted
                        : color.neutral.textSecondary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    transition: 'color 180ms ease',
                  }}
                >
                  {step.label}
                </span>
              )}
            </div>

            {/* Connector — fills brand color when this step is completed */}
            {!isLast && (
              <span
                aria-hidden
                style={{
                  flex: '1 1 auto',
                  height: 2,
                  minWidth: 16,
                  marginInline: spacePx[1],
                  borderRadius: 999,
                  backgroundColor: isCompleted ? color.brand.primary : color.neutral.border,
                  transition: 'background-color 240ms ease',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
